// import node module libraries
import { Fragment, useEffect, useState } from "react";
import { useMutation, useQuery } from "@apollo/client/react";

import { Card, Form, Button, Modal, Stack, Tabs, Tab, Badge } from "react-bootstrap";

// import custom components
import GKAccordionActions from "components/marketing/common/accordions/GKAccordionActions";
import { useNavigate } from "react-router-dom";
import { PATH_DASHBOARD } from "routes/Paths";
import { Notify } from "notiflix";

// Queries
import { GET_COURSE_MODULES } from "helper/graphql/queries";
import {
  CREATE_NEW_MODULE,
  CREATE_NEW_MODULE_CONTENT,
  CREATE_COURSE_RESOURCES,
  DELETE_MODULE_TOPIC,
  DELETE_MODULE_QUIZ,
  DELETE_MODULE_RESOURCE,
  UPDATE_COURSE_MODULE,
  DELETE_MODULE,
} from "helper/graphql/mutations";
import AddQuizForm from "./quiz/AddQuizForm";

// ============================================================================
// ADD LECTURE MODAL
// ============================================================================
const AddLecture = ({ saveLecture, course, isEdit, lecture }) => {
  const [show, setShow] = useState(false);
  const [formData, setFormData] = useState();

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  useEffect(() => {
    if (lecture) {
      setFormData({
        guid: lecture?.guid,
        title: lecture?.title,
        description: lecture?.description,
        mustComplete: lecture?.mustComplete,
      });
    }
  }, [lecture]);

  const handleSave = () => {
    formData.courseId = course;
    saveLecture(formData, () => handleClose(), isEdit);
  };

  const handleChange = (e) => {
    const { name, value, type } = e.target;

    if (type === "file") {
      setFormData((prevState) => ({
        ...prevState,
        [name]: e.target.files,
      }));
    } else {
      setFormData((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    }
  };

  return (
    <Fragment>
      {!isEdit ? (
        <Button variant="" className="add-lecture-btn" onClick={handleShow}>
          <i className="fe fe-plus-circle me-2"></i>
          Add Lecture
        </Button>
      ) : (
        <button type="button" className="icon-edit-btn" onClick={handleShow}>
          <i className="fe fe-edit-2"> Edit</i>
        </button>
      )}

      <Modal show={show} onHide={handleClose} backdrop="static" keyboard={false} centered>
        <Modal.Header closeButton className="lecture-modal-header">
          <div className="modal-title-section">
            <i className="fe fe-book-open modal-icon me-3"></i>
            <div>
              <h5 className="mb-0">{isEdit ? "Edit Lecture" : "Add New Lecture"}</h5>
              <p className="text-muted small mb-0">Create a new module for your course</p>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body className="p-4">
          <Form.Group className="form-group-modern mb-4">
            <Form.Label className="form-label-modern">
              Lecture Title <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              name="title"
              value={formData?.title || ''}
              placeholder="e.g., Introduction to Pediatric Rheumatology"
              onChange={handleChange}
              className="form-control-modern"
            />
          </Form.Group>

          <Form.Group className="form-group-modern mb-4">
            <Form.Label className="form-label-modern">Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={formData?.description || ''}
              name="description"
              placeholder="Provide a brief description of what this lecture covers"
              onChange={handleChange}
              className="form-control-modern"
            />
          </Form.Group>

          <Form.Group className="form-group-modern">
            <div className="completion-toggle">
              <Form.Check
                type="switch"
                name="mustComplete"
                checked={formData?.mustComplete || false}
                onChange={(e) => {
                  setFormData((prevState) => ({
                    ...prevState,
                    [e.target.name]: e.target.checked,
                  }));
                }}
                label={
                  <div>
                    <strong>Require Completion</strong>
                    <small className="d-block text-muted">
                      Students must complete this lecture before proceeding
                    </small>
                  </div>
                }
                className="switch-modern"
              />
            </div>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="modal-footer-modern">
          <Button variant="" className="btn-modal-cancel" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="" className="btn-modal-save" disabled={!formData?.title} onClick={handleSave}>
            <i className="fe fe-check me-2"></i>
            {isEdit ? 'Update Lecture' : 'Create Lecture'}
          </Button>
        </Modal.Footer>
      </Modal>

      <style jsx>{`
        .add-lecture-btn {
          background: linear-gradient(135deg, #F68521 0%, #ff9d4d 100%);
          color: white;
          border: none;
          border-radius: 10px;
          padding: 0.75rem 1.5rem;
          font-weight: 600;
          font-size: 0.9375rem;
          transition: all 0.3s ease;
          display: inline-flex;
          align-items: center;
          box-shadow: 0 4px 12px rgba(246, 133, 33, 0.25);
        }

        .add-lecture-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(246, 133, 33, 0.35);
        }

        .icon-edit-btn {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          border: none;
          background: transparent;
          color: #1d9eb0;
          cursor: pointer;
          transition: all 0.3s ease;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .icon-edit-btn:hover {
          background: rgba(29, 158, 176, 0.1);
          transform: scale(1.1);
        }

        :global(.lecture-modal-header) {
          background: linear-gradient(135deg, #F68521 0%, #ff9d4d 100%);
          color: white;
          border: none;
          padding: 1.5rem 2rem;
        }

        :global(.lecture-modal-header .btn-close) {
          filter: brightness(0) invert(1);
        }

        .modal-title-section {
          display: flex;
          align-items: center;
        }

        .modal-icon {
          font-size: 2rem;
          opacity: 0.9;
        }

        .modal-title-section h5 {
          color: white;
          font-weight: 700;
        }

        .modal-title-section .text-muted {
          color: rgba(255, 255, 255, 0.85) !important;
        }

        .form-group-modern {
          margin-bottom: 1.5rem;
        }

        :global(.form-label-modern) {
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 0.5rem;
          font-size: 0.9375rem;
        }

        :global(.form-control-modern) {
          border: 2px solid #e2e8f0;
          border-radius: 10px;
          padding: 0.75rem 1rem;
          font-size: 0.9375rem;
          transition: all 0.3s ease;
        }

        :global(.form-control-modern:focus) {
          border-color: #F68521;
          box-shadow: 0 0 0 3px rgba(246, 133, 33, 0.1);
        }

        .completion-toggle {
          background: #f8f9fa;
          border: 2px solid #e2e8f0;
          border-radius: 10px;
          padding: 1.25rem;
        }

        :global(.switch-modern .form-check-input) {
          width: 48px;
          height: 24px;
          cursor: pointer;
        }

        :global(.switch-modern .form-check-input:checked) {
          background-color: #F68521;
          border-color: #F68521;
        }

        :global(.modal-footer-modern) {
          border: none;
          padding: 1.5rem 2rem;
          background: #f8f9fa;
        }

        .btn-modal-cancel {
          background: #e2e8f0;
          color: #64748b;
          border: none;
          border-radius: 10px;
          padding: 0.75rem 1.5rem;
          font-weight: 600;
          transition: all 0.3s ease;
        }

        .btn-modal-cancel:hover {
          background: #cbd5e1;
          color: #1e293b;
        }

        .btn-modal-save {
          background: linear-gradient(135deg, #70a553 0%, #8bc86f 100%);
          color: white;
          border: none;
          border-radius: 10px;
          padding: 0.75rem 1.5rem;
          font-weight: 600;
          transition: all 0.3s ease;
          box-shadow: 0 4px 12px rgba(112, 165, 83, 0.25);
        }

        .btn-modal-save:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(112, 165, 83, 0.35);
        }

        .btn-modal-save:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
        }
      `}</style>
    </Fragment>
  );
};

// ============================================================================
// DELETE LECTURE MODAL
// ============================================================================
const DeleteLecture = ({ deleteLecture, lecture }) => {
  const [show, setShow] = useState(false);

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const handleDelete = () => {
    deleteLecture(lecture?.guid, () => handleClose());
  };

  return (
    <Fragment>
      <button className="icon-delete-btn" onClick={handleShow}>
        <i className="fe fe-trash-2"></i>
      </button>

      <Modal show={show} onHide={handleClose} backdrop="static" keyboard={false} centered>
        <Modal.Header closeButton className="delete-modal-header">
          <div className="modal-title-section">
            <i className="fe fe-alert-triangle modal-icon me-3"></i>
            <div>
              <h5 className="mb-0">Delete Lecture</h5>
              <p className="text-muted small mb-0">This action cannot be undone</p>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body className="p-4">
          <p className="mb-2">
            Are you sure you want to delete <strong>"{lecture?.title}"</strong>?
          </p>
          <p className="text-muted small mb-0">
            All topics, quizzes, and resources within this lecture will also be permanently deleted.
          </p>
        </Modal.Body>
        <Modal.Footer className="modal-footer-modern">
          <Button variant="" className="btn-modal-cancel" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="" className="btn-modal-delete" onClick={handleDelete}>
            <i className="fe fe-trash-2 me-2"></i>
            Delete Lecture
          </Button>
        </Modal.Footer>
      </Modal>

      <style jsx>{`
        .icon-delete-btn {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          border: none;
          background: transparent;
          color: #dc2626;
          cursor: pointer;
          transition: all 0.3s ease;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .icon-delete-btn:hover {
          background: rgba(220, 38, 38, 0.1);
          transform: scale(1.1);
        }

        :global(.delete-modal-header) {
          background: linear-gradient(135deg, #dc2626 0%, #ef4444 100%);
          color: white;
          border: none;
          padding: 1.5rem 2rem;
        }

        :global(.delete-modal-header .btn-close) {
          filter: brightness(0) invert(1);
        }

        .btn-modal-delete {
          background: linear-gradient(135deg, #dc2626 0%, #ef4444 100%);
          color: white;
          border: none;
          border-radius: 10px;
          padding: 0.75rem 1.5rem;
          font-weight: 600;
          transition: all 0.3s ease;
          box-shadow: 0 4px 12px rgba(220, 38, 38, 0.25);
        }

        .btn-modal-delete:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(220, 38, 38, 0.35);
        }
      `}</style>
    </Fragment>
  );
};

// ============================================================================
// ADD SECTION/TOPIC MODAL
// ============================================================================
const AddSection = ({ saveSection, module }) => {
  const [show, setShow] = useState(false);
  const [formData, setFormData] = useState();

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSave = () => {
    const payload = {
      moduleId: module?.guid,
      title: formData.title,
      description: formData.description,
    };
    saveSection(payload, () => handleClose());
  };

  return (
    <Fragment>
      <Button variant="" className="add-topic-btn" onClick={handleShow}>
        <i className="fe fe-file-text me-2"></i>
        Topic
      </Button>

      <Modal show={show} onHide={handleClose} backdrop="static" keyboard={false} centered>
        <Modal.Header closeButton className="topic-modal-header">
          <div className="modal-title-section">
            <i className="fe fe-file-text modal-icon me-3"></i>
            <div>
              <h5 className="mb-0">Add New Topic</h5>
              <p className="text-muted small mb-0">Add content to this lecture</p>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body className="p-4">
          <Form.Group className="form-group-modern mb-4">
            <Form.Label className="form-label-modern">
              Topic Title <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              name="title"
              placeholder="e.g., Understanding Joint Inflammation"
              onChange={handleChange}
              className="form-control-modern"
            />
          </Form.Group>

          <Form.Group className="form-group-modern">
            <Form.Label className="form-label-modern">Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              name="description"
              placeholder="Describe what this topic covers"
              onChange={handleChange}
              className="form-control-modern"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="modal-footer-modern">
          <Button variant="" className="btn-modal-cancel" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="" className="btn-modal-save" disabled={!formData?.title} onClick={handleSave}>
            <i className="fe fe-check me-2"></i>
            Add Topic
          </Button>
        </Modal.Footer>
      </Modal>

      <style jsx>{`
        .add-topic-btn {
          background: rgba(29, 158, 176, 0.1);
          border: 1px solid #1d9eb0;
          color: #1d9eb0;
          border-radius: 8px;
          padding: 0.5rem 1rem;
          font-weight: 600;
          font-size: 0.875rem;
          transition: all 0.3s ease;
          display: inline-flex;
          align-items: center;
        }

        .add-topic-btn:hover {
          background: #1d9eb0;
          border-color: #1d9eb0;
          color: white;
          transform: translateY(-2px);
        }

        :global(.topic-modal-header) {
          background: linear-gradient(135deg, #1d9eb0 0%, #3fc1d3 100%);
          color: white;
          border: none;
          padding: 1.5rem 2rem;
        }

        :global(.topic-modal-header .btn-close) {
          filter: brightness(0) invert(1);
        }
      `}</style>
    </Fragment>
  );
};

// ============================================================================
// ADD RESOURCES MODAL
// ============================================================================
const AddResources = ({ module, refetch, contentType }) => {
  const [show, setShow] = useState(false);
  const [formData, setFormData] = useState();
  const [fileName, setFileName] = useState('');

  const [onCreateResources, { loading: isModuleContentLoad }] = useMutation(
    CREATE_COURSE_RESOURCES,
    {
      context: {
        Headers: {
          "Apollo-Require-Preflight": true,
        },
      },
    }
  );

  const handleClose = () => {
    setShow(false);
    setFileName('');
  };
  const handleShow = () => setShow(true);

  const handleFileChange = (e) => {
    const { files } = e.target;
    if (files && files[0]) {
      setFileName(files[0].name);
      setFormData((prevState) => ({
        ...prevState,
        [contentType]: files[0],
      }));
    }
  };

  const onAddLink = (e) => {
    setFormData((prevState) => ({
      ...prevState,
      [contentType]: e.target.value,
    }));
  };

  const handleSave = () => {
    const payload = {
      moduleGuid: module?.guid,
      contentType: contentType,
      files: formData?.files || [],
      videos: formData?.videos || [],
      images: formData?.images || [],
      links: formData?.links || "",
    };

    onCreateResources({
      variables: payload,
      awaitRefetchQueries: true,
      onCompleted: () => {
        handleClose();
        Notify.success('Resource added successfully');
        refetch();
      },
      onError: () => {
        Notify.failure("Failed to add resource!");
      }
    });
  };

  const getResourceConfig = () => {
    const configs = {
      files: {
        icon: 'fe-file',
        title: 'Files',
        accept: '.pdf,.pptx,.docx',
        description: 'PDF, PowerPoint, or Word documents',
        color: '#1d9eb0'
      },
      videos: {
        icon: 'fe-video',
        title: 'Videos',
        accept: 'video/*',
        description: 'MP4, AVI, MOV (HD quality recommended)',
        color: '#F68521'
      },
      images: {
        icon: 'fe-image',
        title: 'Images',
        accept: 'image/png, image/gif, image/jpeg',
        description: 'PNG, JPG, or JPEG (750x440px recommended)',
        color: '#70a553'
      },
      links: {
        icon: 'fe-link',
        title: 'Links',
        color: '#7c3aed'
      }
    };
    return configs[contentType] || configs.files;
  };

  const config = getResourceConfig();

  return (
    <Fragment>
      <Button variant="" className={`add-resource-btn resource-${contentType}`} onClick={handleShow}>
        <i className={`fe ${config.icon} me-2`}></i>
        {config.title}
      </Button>

      <Modal show={show} onHide={handleClose} backdrop="static" keyboard={false} centered>
        <Modal.Header closeButton className="resource-modal-header" style={{ background: `linear-gradient(135deg, ${config.color} 0%, ${config.color}dd 100%)` }}>
          <div className="modal-title-section">
            <i className={`fe ${config.icon} modal-icon me-3`}></i>
            <div>
              <h5 className="mb-0">Add {config.title}</h5>
              <p className="text-muted small mb-0">Upload learning resources</p>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body className="p-4">
          {contentType !== "links" ? (
            <div className="upload-zone">
              <input
                type="file"
                id={`resource-${contentType}`}
                name={contentType}
                accept={config.accept}
                onChange={handleFileChange}
                className="file-input-hidden"
              />
              <label htmlFor={`resource-${contentType}`} className="upload-label">
                {fileName ? (
                  <div className="file-selected">
                    <i className={`fe ${config.icon} file-icon mb-3`} style={{ color: config.color }}></i>
                    <h6 className="mb-2">{fileName}</h6>
                    <p className="text-muted small mb-0">File ready to upload</p>
                    <Button variant="link" className="change-file-btn mt-2">
                      <i className="fe fe-edit-2 me-2"></i>
                      Change File
                    </Button>
                  </div>
                ) : (
                  <div className="upload-placeholder">
                    <i className={`fe ${config.icon} upload-icon mb-3`}></i>
                    <h6 className="mb-2">Click to upload or drag and drop</h6>
                    <p className="upload-specs">{config.description}</p>
                  </div>
                )}
              </label>
            </div>
          ) : (
            <Form.Group className="form-group-modern">
              <Form.Label className="form-label-modern">
                Resource URL <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="url"
                placeholder="https://example.com/resource"
                onChange={onAddLink}
                className="form-control-modern"
              />
              <Form.Text className="text-muted">
                <i className="fe fe-info me-1"></i>
                Add external resources like YouTube videos, articles, or websites
              </Form.Text>
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer className="modal-footer-modern">
          <Button variant="" className="btn-modal-cancel" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant=""
            className="btn-modal-save"
            disabled={!formData || isModuleContentLoad}
            onClick={handleSave}
          >
            {isModuleContentLoad ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Uploading...
              </>
            ) : (
              <>
                <i className="fe fe-upload me-2"></i>
                Upload {config.title}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <style jsx>{`
        .add-resource-btn {
          border-radius: 8px;
          padding: 0.5rem 1rem;
          font-weight: 600;
          font-size: 0.875rem;
          transition: all 0.3s ease;
          display: inline-flex;
          align-items: center;
          border: 1px solid;
        }

        .resource-files {
          background: rgba(29, 158, 176, 0.1);
          border-color: #1d9eb0;
          color: #1d9eb0;
        }

        .resource-files:hover {
          background: #1d9eb0;
          color: white;
        }

        .resource-videos {
          background: rgba(246, 133, 33, 0.1);
          border-color: #F68521;
          color: #F68521;
        }

        .resource-videos:hover {
          background: #F68521;
          color: white;
        }

        .resource-images {
          background: rgba(112, 165, 83, 0.1);
          border-color: #70a553;
          color: #70a553;
        }

        .resource-images:hover {
          background: #70a553;
          color: white;
        }

        .resource-links {
          background: rgba(124, 58, 237, 0.1);
          border-color: #7c3aed;
          color: #7c3aed;
        }

        .resource-links:hover {
          background: #7c3aed;
          color: white;
        }

        :global(.resource-modal-header) {
          color: white;
          border: none;
          padding: 1.5rem 2rem;
        }

        :global(.resource-modal-header .btn-close) {
          filter: brightness(0) invert(1);
        }

        .upload-zone {
          border: 2px dashed #cbd5e1;
          border-radius: 12px;
          padding: 2rem;
          text-align: center;
          background: #f8f9fa;
          transition: all 0.3s ease;
        }

        .upload-zone:hover {
          border-color: #94a3b8;
          background: #f1f5f9;
        }

        .file-input-hidden {
          position: absolute;
          opacity: 0;
          width: 0;
          height: 0;
        }

        .upload-label {
          cursor: pointer;
          margin: 0;
          display: block;
        }

        .upload-icon {
          font-size: 3rem;
          color: #cbd5e1;
        }

        .file-icon {
          font-size: 3rem;
        }

        .upload-specs {
          color: #94a3b8;
          font-size: 0.8125rem;
          margin: 0;
        }

        :global(.change-file-btn) {
          color: #1d9eb0;
          text-decoration: none;
        }
      `}</style>
    </Fragment>
  );
};

// ============================================================================
// ADD QUIZ MODAL
// ============================================================================
const AddQuiz = ({ module, refetch, courseGuid }) => {
  const [show, setShow] = useState(false);

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  return (
    <Fragment>
      <Button variant="" className="add-quiz-btn" onClick={handleShow}>
        <i className="fe fe-help-circle me-2"></i>
        Quiz
      </Button>

      <Modal show={show} size="xl" onHide={handleClose} backdrop="static" keyboard={false}>
        <AddQuizForm
          onSuccess={() => {
            handleClose();
            refetch();
          }}
          module={module}
          courseGuid={courseGuid}
        />
      </Modal>

      <style jsx>{`
        .add-quiz-btn {
          background: rgba(124, 58, 237, 0.1);
          border: 1px solid #7c3aed;
          color: #7c3aed;
          border-radius: 8px;
          padding: 0.5rem 1rem;
          font-weight: 600;
          font-size: 0.875rem;
          transition: all 0.3s ease;
          display: inline-flex;
          align-items: center;
        }

        .add-quiz-btn:hover {
          background: #7c3aed;
          border-color: #7c3aed;
          color: white;
          transform: translateY(-2px);
        }
      `}</style>
    </Fragment>
  );
};

// ============================================================================
// MAIN CURRICULUM COMPONENT
// ============================================================================
const Curriculum = (props) => {
  const navigate = useNavigate();
  const { course } = props;

  const [onCreateModule, { loading: isModuleLoad }] = useMutation(CREATE_NEW_MODULE);
  const [onUpdateModule] = useMutation(UPDATE_COURSE_MODULE);
  const [onDeleteModule] = useMutation(DELETE_MODULE);

  const [onCreateModuleContent, { loading: isModuleContentLoad }] = useMutation(
    CREATE_NEW_MODULE_CONTENT,
    {
      context: {
        Headers: {
          "Apollo-Require-Preflight": true,
        },
      },
    }
  );
  const [onDeleteSection] = useMutation(DELETE_MODULE_TOPIC);
  const [onDeleteQuiz] = useMutation(DELETE_MODULE_QUIZ);
  const [onDeleteRes] = useMutation(DELETE_MODULE_RESOURCE);

  const [sections, setSections] = useState([]);

  const { refetch, data: _sections } = useQuery(GET_COURSE_MODULES, {
    variables: { courseGuid: course },
    onCompleted: (data) => {
      const _modules =
        data?.allCourseModules?.edges?.map((item) => item.node) || [];
      setSections(_modules);
    },
  });

  const handleNewSection = (data, handleClose) => {
    onCreateModuleContent({
      variables: data,
      awaitRefetchQueries: true,
      onCompleted: () => {
        Notify.success("Topic added successfully!");
        refetch();
        handleClose();
      },
      onError: () => {
        Notify.failure("Failed to add Topic! Kindly enter some description");
      },
    });
  };

  const handleDeleteLecture = (guid, handleClose) => {
    onDeleteModule({
      variables: { guid: guid },
      awaitRefetchQueries: true,
      onCompleted: () => {
        Notify.success("Lecture Deleted Successfully!");
        refetch();
        handleClose();
      },
      onError: () => {
        Notify.failure("Failed to delete Lecture");
      },
    });
  };

  const handleNewLecture = (data, handleClose, isEdit) => {
    if (isEdit) {
      onUpdateModule({
        variables: data,
        awaitRefetchQueries: true,
        onCompleted: () => {
          Notify.success("Lecture Updated!");
          refetch();
          handleClose();
        },
        onError: () => {
          Notify.failure("Failed to update Lecture");
        },
      });
    } else {
      onCreateModule({
        variables: data,
        awaitRefetchQueries: true,
        onCompleted: () => {
          Notify.success("Lecture Saved!");
          refetch();
          handleClose();
        },
        onError: () => {
          Notify.failure("Failed to save Lecture");
        },
      });
    }
  };

  useEffect(() => {
    const _modules = _sections?.allCourseModules?.edges?.map(
      (item) => item.node
    );
    if (_modules) {
      setSections(_modules);
    }
  }, [_sections]);

  const handleDeleteTopic = (guid) => {
    onDeleteSection({
      variables: { guid: guid },
      awaitRefetchQueries: true,
      onCompleted: () => {
        Notify.success("Topic deleted!");
        refetch();
      },
      onError: () => {
        Notify.failure("Failed to delete!");
      },
    });
  };

  const handleDeleteQuiz = (guid) => {
    onDeleteQuiz({
      variables: { guid: guid },
      awaitRefetchQueries: true,
      onCompleted: () => {
        Notify.success("Quiz deleted!");
        refetch();
      },
      onError: () => {
        Notify.failure("Failed to delete!");
      },
    });
  };

  const handleDeleteResource = (guid) => {
    onDeleteRes({
      variables: { guid: guid },
      awaitRefetchQueries: true,
      onCompleted: () => {
        Notify.success("Resource deleted!");
        refetch();
      },
      onError: () => {
        Notify.failure("Failed to delete!");
      },
    });
  };

  return (
    <Form>
      {/* Main Card */}
      <Card className="curriculum-card mb-4">
        <Card.Header className="curriculum-header">
          <div className="d-flex align-items-center">
            <div className="header-icon me-3">
              <i className="fe fe-layers"></i>
            </div>
            <div>
              <h4 className="mb-1">Course Curriculum</h4>
              <p className="text-muted mb-0 small">
                Organize your course content into lectures, topics, and resources
              </p>
            </div>
          </div>
        </Card.Header>

        <Card.Body className="curriculum-body">
          {sections.length === 0 ? (
            <div className="empty-curriculum">
              <div className="empty-icon">
                <i className="fe fe-layers"></i>
              </div>
              <h5 className="mb-2">No Lectures Yet</h5>
              <p className="text-muted mb-4">
                Start building your course by adding your first lecture module
              </p>
              <AddLecture
                saveLecture={(data, handleClose) =>
                  handleNewLecture(data, handleClose)
                }
                course={course}
              />
            </div>
          ) : (
            <>
              {sections.map((item, index) => (
                <div className="lecture-module" key={item?.guid}>
                  <div className="lecture-header">
                    <div className="lecture-info">
                      <Badge bg="" className="lecture-badge">
                        Lecture {index + 1}
                      </Badge>
                      <h4 className="lecture-title">{item?.title}</h4>
                    </div>

                    <div className="lecture-actions">
                      <AddLecture
                        saveLecture={(data, handleClose, isEdit) =>
                          handleNewLecture(data, handleClose, isEdit)
                        }
                        isEdit={true}
                        course={course}
                        lecture={item}
                      />

                      {/* only show delete lecuture component if there are no sections, topics,resources, quizzes, or resources */}
                      {(item?.coursemodulecontentsSet?.edges?.length === 0 &&
                        item?.modulecontentquizSet?.edges?.length === 0 &&
                        item?.coursemoduleresourcesSet?.edges?.length === 0) && (
                        <DeleteLecture
                          deleteLecture={(guid, handleClose) =>
                            handleDeleteLecture(guid, handleClose)
                          }
                          lecture={item}
                        />
                      )}
                      
                     
                    </div>
                  </div>

                  {item?.description && (
                    <p className="lecture-description">{item?.description}</p>
                  )}

                  {/* Tabs for content types */}
                  <Tabs
                    defaultActiveKey="topics"
                    className="curriculum-tabs"
                  >
                    <Tab eventKey="topics" title={
                      <span>
                        <i className="fe fe-file-text me-2"></i>
                        Topics ({item?.coursemodulecontentsSet?.edges?.length || 0})
                      </span>
                    }>
                      <div className="tab-content-area">
                        <GKAccordionActions
                          accordionItems={item?.coursemodulecontentsSet?.edges.map(
                            (item) => item.node
                          )}
                          handleDelete={(data) => handleDeleteTopic(data)}
                        />
                      </div>
                    </Tab>
                    <Tab eventKey="resources" title={
                      <span>
                        <i className="fe fe-paperclip me-2"></i>
                        Resources ({item?.coursemoduleresourcesSet?.edges?.length || 0})
                      </span>
                    }>
                      <div className="tab-content-area">
                        <GKAccordionActions
                          accordionItems={item?.coursemoduleresourcesSet?.edges.map(
                            (item) => item.node
                          )}
                          handleDelete={(data) => handleDeleteResource(data)}
                        />
                      </div>
                    </Tab>
                                        <Tab eventKey="quiz" title={
                      <span>
                        <i className="fe fe-help-circle me-2"></i>
                        Quizzes ({item?.modulecontentquizSet?.edges?.length || 0})
                      </span>
                    }>
                      <div className="tab-content-area">
                        <GKAccordionActions
                          accordionItems={item?.modulecontentquizSet?.edges.map(
                            (item) => item.node
                          )}
                          module={item}
                          handleDelete={(data) => handleDeleteQuiz(data)}
                          isQuiz
                        />
                      </div>
                    </Tab>
                  </Tabs>

                  {/* Action buttons */}
                  <div className="content-actions">
                    <div className="action-label">
                      <i className="fe fe-plus-circle me-2"></i>
                      Add Content:
                    </div>
                    <Stack direction="horizontal" gap={2} className="flex-wrap">
                      <AddSection
                        saveSection={(data, handleClose) =>
                          handleNewSection(data, handleClose)
                        }
                        module={item}
                      />
                      <AddResources contentType={"files"} module={item} refetch={refetch} />
                      <AddResources contentType={"videos"} module={item} refetch={refetch} />
                      <AddResources contentType={"images"} module={item} refetch={refetch} />
                      <AddResources contentType={"links"} module={item} refetch={refetch} />
                      <AddQuiz module={item} refetch={refetch} courseGuid={course} />
                    </Stack>
                  </div>
                </div>
              ))}

              <div className="add-lecture-section">
                <AddLecture
                  saveLecture={(data, handleClose) =>
                    handleNewLecture(data, handleClose)
                  }
                  course={course}
                />
              </div>
            </>
          )}
        </Card.Body>
      </Card>

      {/* Finish Button */}
      <div className="curriculum-footer">
        <Button
          variant=""
          className="btn-finish"
          onClick={() => {
            Notify.success('Successful! Proceed to Dashboard to submit for Review');
            navigate(PATH_DASHBOARD.instructor.courses)}}
          disabled={isModuleContentLoad || isModuleLoad}
        >
          {isModuleContentLoad || isModuleLoad ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" />
              Processing...
            </>
          ) : (
            <>
              <i className="fe fe-check-circle me-2"></i>
              Finish
            </>
          )}
        </Button>
      </div>

      <style jsx>{`
        /* Main Card */
        :global(.curriculum-card) {
          border: none;
          border-radius: 16px;
          box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
          overflow: hidden;
        }

        :global(.curriculum-header) {
          background: linear-gradient(135deg, #7c3aed 0%, #a855f7 100%);
          color: white;
          padding: 2rem;
          border: none;
        }

        .header-icon {
          width: 48px;
          height: 48px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
        }

        :global(.curriculum-header) h4 {
          color: white;
          margin: 0;
          font-weight: 700;
        }

        :global(.curriculum-body) {
          padding: 2rem;
        }

        /* Empty State */
        .empty-curriculum {
          text-align: center;
          padding: 4rem 2rem;
          background: #f8f9fa;
          border-radius: 12px;
          border: 2px dashed #e2e8f0;
        }

        .empty-icon {
          font-size: 4rem;
          color: #cbd5e1;
          margin-bottom: 1.5rem;
        }

        /* Lecture Module */
        .lecture-module {
          background: white;
          border: 2px solid #e2e8f0;
          border-radius: 16px;
          padding: 1.5rem;
          margin-bottom: 1.5rem;
          transition: all 0.3s ease;
        }

        .lecture-module:hover {
          border-color: #cbd5e1;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
        }

        .lecture-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 1rem;
          gap: 1rem;
        }

        .lecture-info {
          flex: 1;
        }

        :global(.lecture-badge) {
          background: rgba(124, 58, 237, 0.1) !important;
          color: #7c3aed !important;
          padding: 6px 12px;
          border-radius: 6px;
          font-size: 0.75rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 0.75rem;
          display: inline-block;
        }

        .lecture-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1e293b;
          margin: 0;
        }

        .lecture-actions {
          display: flex;
          gap: 0.5rem;
          align-items: center;
        }

        .lecture-description {
          color: #64748b;
          margin-bottom: 1.5rem;
          line-height: 1.6;
        }

        /* Tabs */
        :global(.curriculum-tabs) {
          border-bottom: 2px solid #e2e8f0;
          margin-bottom: 1.5rem;
        }

        :global(.curriculum-tabs .nav-link) {
          border: none;
          color: #64748b;
          font-weight: 600;
          padding: 0.75rem 1.25rem;
          transition: all 0.3s ease;
        }

        :global(.curriculum-tabs .nav-link:hover) {
          color: #7c3aed;
          background: rgba(124, 58, 237, 0.05);
        }

        :global(.curriculum-tabs .nav-link.active) {
          color: #7c3aed;
          border-bottom: 3px solid #7c3aed;
          background: transparent;
        }

        .tab-content-area {
          padding: 1.5rem 0;
          min-height: 100px;
        }

        /* Content Actions */
        .content-actions {
          margin-top: 1.5rem;
          padding-top: 1.5rem;
          border-top: 2px solid #f1f5f9;
        }

        .action-label {
          font-size: 0.875rem;
          font-weight: 600;
          color: #64748b;
          margin-bottom: 0.75rem;
        }

        /* Add Lecture Section */
        .add-lecture-section {
          text-align: center;
          padding: 2rem;
          background: #f8f9fa;
          border-radius: 12px;
          border: 2px dashed #e2e8f0;
        }

        /* Footer */
        .curriculum-footer {
          display: flex;
          justify-content: flex-end;
          margin-top: 2rem;
        }

        .btn-finish {
          background: linear-gradient(135deg, #70a553 0%, #8bc86f 100%);
          color: white;
          border: none;
          border-radius: 12px;
          padding: 1rem 2.5rem;
          font-weight: 700;
          font-size: 1rem;
          transition: all 0.3s ease;
          box-shadow: 0 4px 15px rgba(112, 165, 83, 0.3);
        }

        .btn-finish:hover {
          transform: translateY(-3px);
          box-shadow: 0 8px 20px rgba(112, 165, 83, 0.4);
        }

        .btn-finish:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        /* Responsive */
        @media (max-width: 991px) {
          :global(.curriculum-header) {
            padding: 1.5rem;
          }

          :global(.curriculum-body) {
            padding: 1.5rem;
          }

          .lecture-module {
            padding: 1.25rem;
          }

          .lecture-header {
            flex-direction: column;
          }

          .lecture-actions {
            width: 100%;
            justify-content: flex-start;
          }

          .content-actions Stack {
            flex-direction: column;
            align-items: stretch;
          }

          .curriculum-footer {
            justify-content: stretch;
          }

          .btn-finish {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </Form>
  );
};

export default Curriculum;